import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import {
  defaultPeriodForYear, formatDate, formatMoney, formatPeriod,
  formatPeriodRange, yearOfPeriod,
} from '../lib/format'
import { useSelectedYear } from '../year/useYear'
import { chargesForPeriod, type LeaseForCharges } from '../lib/rules'
import {
  computeWaterfall, chargesInForce, type RecurringCharge,
} from '../lib/waterfall'
import { canEnterData } from '../lib/types'
import type { RentStatusRow } from '../lib/types'
import { Loading, ErrorState, EmptyState } from '../components/States'
import { ChargeSheet } from './ChargeSheet'

/** Mois precedent ou suivant, selon la regle du 5 au 5. */
function shiftPeriod(period: string, delta: number): string {
  const [y, m] = period.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + delta, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

interface MonthData {
  rows: RentStatusRow[]
  /** Revenu net du mois, apres charges et depenses approuvees. */
  net: number
  chargesTotal: number
  expensesApproved: number
  chargesConfigured: boolean
}

async function loadMonth(period: string): Promise<MonthData> {
  const [rent, expenses, charges] = await Promise.all([
    supabase
      .from('v_rent_status')
      .select('*')
      .eq('period', period)
      .order('unit_name', { ascending: true }),
    supabase
      .from('expenses')
      .select('amount')
      .eq('period', period)
      .eq('status', 'approuve'),
    // Les charges mensuelles peuvent ne pas etre configurees : l'ecran
    // des loyers ne doit pas tomber en panne pour autant, c'est celui
    // qui sert tous les jours.
    supabase
      .from('recurring_charges')
      .select(
        'code, label_fr, label_en, kind, rate, fixed_amount, base, payee,' +
          ' requires_receipt, sort_order, a_verifier,' +
          ' effective_from, effective_to, active',
      ),
  ])

  if (rent.error) throw rent.error

  const rows = (rent.data ?? []) as RentStatusRow[]
  const encaisse = rows.reduce((s, r) => s + r.amount_received, 0)

  const expensesApproved = expenses.error
    ? 0
    : (expenses.data ?? []).reduce(
        (s, e) => s + Number((e as { amount: number }).amount ?? 0),
        0,
      )

  type ChargeRow = RecurringCharge & {
    effective_from: string; effective_to: string | null; active: boolean
    label_fr: string
  }
  const toutes = (charges.error ? [] : (charges.data ?? [])) as unknown as ChargeRow[]
  const enForce = chargesInForce(toutes, period).map((c) => ({
    ...c,
    label: c.label_fr,
    rate: c.rate === null ? null : Number(c.rate),
  }))

  const w = computeWaterfall(encaisse, enForce, expensesApproved)

  return {
    rows,
    net: w.netToDeposit,
    chargesTotal: w.recurringTotal,
    expensesApproved,
    chargesConfigured: toutes.length > 0,
  }
}

export function RentScreen() {
  const { t, lang } = useI18n()
  const { profile } = useAuth()
  const { year } = useSelectedYear()
  // Le mois affiche reste toujours dans l'annee choisie : les fleches
  // s'arretent a janvier et a decembre. Aucun ecran ne peut donc
  // presenter un mois d'une autre annee que celle du bandeau.
  const [period, setPeriod] = useState(() => defaultPeriodForYear(year))
  const [charge, setCharge] = useState<RentStatusRow | null>(null)
  const [opening, setOpening] = useState(false)
  const [opened, setOpened] = useState<string | null>(null)
  const [openError, setOpenError] = useState<string | null>(null)

  const { data, loading, error, reload } = useAsync(
    () => loadMonth(period),
    [period],
  )

  const peutSaisir = canEnterData(profile?.role ?? null)
  const rows = data?.rows ?? []

  const totalAttendu = rows
    .filter((r) => !r.covered_by_advance)
    .reduce((s, r) => s + r.amount_due, 0)
  const totalRecu = rows.reduce((s, r) => s + r.amount_received, 0)

  /** Cree la ligne de loyer attendu pour chaque bail concerne. */
  async function openMonth() {
    setOpenError(null)
    setOpening(true)

    const { data: leases, error: lErr } = await supabase
      .from('leases')
      .select('id, rent_ttc, advance_month, start_date, end_date, status')
      .in('status', ['actif', 'expire'])

    if (lErr) {
      setOpening(false)
      setOpenError(`${t('save_failed')} ${lErr.message}`)
      return
    }

    const aCreer = chargesForPeriod(period, (leases ?? []) as LeaseForCharges[])
    if (aCreer.length === 0) {
      setOpening(false)
      setOpenError(t('rent_month_not_open_body'))
      return
    }

    // ignoreDuplicates : rouvrir un mois deja ouvert ne cree pas de
    // doublon, la contrainte unique (bail, periode) etant respectee.
    const { error: iErr } = await supabase
      .from('rent_charges')
      .upsert(aCreer, { onConflict: 'lease_id,period', ignoreDuplicates: true })

    setOpening(false)
    if (iErr) {
      setOpenError(`${t('save_failed')} ${iErr.message}`)
      return
    }
    setOpened(t('rent_opened', { n: aCreer.length }))
    reload()
  }

  return (
    <div className="screen">
      <h1 className="screen__title">{formatPeriod(period, lang)}</h1>
      <p className="muted">{formatPeriodRange(period, lang)}</p>

      {/* Navigation de mois, bornee a l'annee choisie. */}
      <div className="monthnav">
        <button
          type="button"
          className="btn btn--ghost"
          disabled={yearOfPeriod(period) !== year || period.endsWith('-01')}
          onClick={() => {
            const prev = shiftPeriod(period, -1)
            if (yearOfPeriod(prev) === year) {
              setPeriod(prev)
              setOpened(null)
            }
          }}
        >
          ← {t('previous')}
        </button>
        <span className="monthnav__label">{formatPeriod(period, lang)}</span>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={period.endsWith('-12')}
          onClick={() => {
            const next = shiftPeriod(period, 1)
            if (yearOfPeriod(next) === year) {
              setPeriod(next)
              setOpened(null)
            }
          }}
        >
          {t('next')} →
        </button>
      </div>

      {loading && <Loading />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && rows.length === 0 && (
        <EmptyState
          title={t('rent_month_not_open')}
          body={t('rent_month_not_open_body')}
          action={
            peutSaisir ? (
              <button
                type="button"
                className="btn btn--primary"
                disabled={opening}
                onClick={() => void openMonth()}
              >
                {opening ? t('saving') : t('rent_open_month')}
              </button>
            ) : undefined
          }
        />
      )}

      {openError && <p className="alert alert--error">{openError}</p>}
      {opened && <p className="alert alert--ok">{opened}</p>}

      {rows.length > 0 && (
        <>
          <div className="totals">
            <div>
              <span className="totals__label">{t('rent_total_expected')}</span>
              <span className="totals__value">{formatMoney(totalAttendu)}</span>
            </div>
            <div>
              <span className="totals__label">{t('rent_total_received')}</span>
              <span className="totals__value totals__value--accent">
                {formatMoney(totalRecu)}
              </span>
            </div>
          </div>

          {/* LE REVENU NET DU MOIS : ce qui reste apres les charges
              mensuelles et les depenses approuvees. La soustraction est
              ecrite dessous, pour etre verifiable sans calculatrice. */}
          {data && (
            <div className="card">
              <span className="card__caption">{t('report_net_revenue')}</span>
              <span className="card__figure">{formatMoney(data.net)}</span>
              <p className="muted">
                {formatMoney(totalRecu)} − {formatMoney(data.chargesTotal)}{' '}
                <span className="muted">({t('recurring_title')})</span> −{' '}
                {formatMoney(data.expensesApproved)}{' '}
                <span className="muted">({t('expense_total_approved')})</span>
              </p>
              {!data.chargesConfigured && (
                <p className="field__error">{t('home_no_charges')}</p>
              )}
              {data.net < 0 && (
                <p className="field__error">{t('settlement_negative')}</p>
              )}
            </div>
          )}

          <ul className="list">
            {rows.map((r) => (
              <li key={r.charge_id}>
                <button
                  type="button"
                  className="list__item list__item--tappable"
                  onClick={() => setCharge(r)}
                >
                  <span className="list__main">
                    {/* Noms issus de la base, jamais traduits. */}
                    <span className="list__name">{r.unit_name}</span>
                    <span className="list__meta">{r.tenant_name}</span>
                    {r.last_payment_on && (
                      <span className="list__note">
                        {formatDate(r.last_payment_on)}
                      </span>
                    )}
                    {r.covered_by_advance && (
                      <span className="list__note">{t('rent_covered_advance')}</span>
                    )}
                  </span>

                  <span className="list__side">
                    <span className="list__amount">
                      {formatMoney(r.amount_received)}
                    </span>
                    <span className="list__meta">
                      / {formatMoney(r.amount_due)}
                    </span>
                    <span
                      className={
                        'chip ' +
                        (r.status === 'paye'
                          ? 'chip--ok'
                          : r.status === 'partiel'
                            ? 'chip--warn'
                            : 'chip--error')
                      }
                    >
                      {t(`rent_status_${r.status}` as
                        'rent_status_paye' | 'rent_status_partiel' | 'rent_status_impaye')}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {charge && (
        <ChargeSheet
          row={charge}
          onClose={() => setCharge(null)}
          onSaved={() => {
            setCharge(null)
            reload()
          }}
        />
      )}
    </div>
  )
}
